import type { CommunityWorkflowConfiguration, WorkflowType } from '@communities/content-management/domain';
import { useCommunityWorkflows, useUpsertCommunityWorkflow } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import { isDefined } from '@core/utils/type-guards';
import { TextInput, LoadingSpinner, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

interface ApiSettingsDialogProps {
  communityId: string;
  workflowType: WorkflowType;
  isOpen?: boolean;
  maxTps?: number | undefined;
  onClose: () => void;
}

/**
 * Dialog component for editing API settings (MAX TPS only)
 */
export function ApiSettingsDialog({ communityId, workflowType, isOpen = false, maxTps, onClose }: ApiSettingsDialogProps) {
  const { workflows } = useCommunityWorkflows(communityId, workflowType);
  const { upsertWorkflow, loading: isSubmitting, errors, resetErrors } = useUpsertCommunityWorkflow();
  const [currentMaxTps, setCurrentMaxTps] = useState(maxTps);

  useEffect(() => {
    if (isOpen) {
      setCurrentMaxTps(maxTps);
      resetErrors();
    }
  }, [maxTps, isOpen, resetErrors]);

  // eslint-disable-next-line complexity
  const handleSubmit = async () => {
    resetErrors();

    if (!isFormValid) {
      return;
    }

    // Create updated workflow configuration that preserves existing settings
    const updatedWorkflow: CommunityWorkflowConfiguration = {
      type: workflowType,
      topicActions: workflows?.topicActions || [],
      apiSettings: {
        accessClients: workflows?.apiSettings?.accessClients || [],
        monthlyBudget: workflows?.apiSettings?.monthlyBudget,
        maxTps: currentMaxTps,
        ...(workflows?.apiSettings?.contentTypeTps && {
          contentTypeTps: {
            ...(isDefined(workflows.apiSettings.contentTypeTps.text) && { text: workflows.apiSettings.contentTypeTps.text }),
            ...(isDefined(workflows.apiSettings.contentTypeTps.image) && { image: workflows.apiSettings.contentTypeTps.image }),
            ...(isDefined(workflows.apiSettings.contentTypeTps.audio) && { audio: workflows.apiSettings.contentTypeTps.audio }),
          },
        }),
      },
    };

    const success = await upsertWorkflow(communityId, workflowType, updatedWorkflow);

    if (success) {
      onClose();
    }
  };

  // Handle max TPS change
  const handleMaxTpsChange = (value: string) => {
    const newMaxTps = parseInt(value, 10) || 0;

    setCurrentMaxTps(newMaxTps);
  };

  // Check if form is valid
  const isFormValid = currentMaxTps && currentMaxTps > 0;

  const dialogTitle = `${workflowType.charAt(0).toUpperCase() + workflowType.slice(1)} API Settings`;
  const dialogDescription = `Configure API settings for the ${workflowType} workflow`;

  return (
    <Dialog
      description={dialogDescription}
      isOpen={isOpen}
      primaryAction={{
        label: isSubmitting ? <LoadingSpinner /> : 'Update Settings',
        onClick: handleSubmit,
        disabled: isSubmitting || !isFormValid,
      }}
      size="md"
      title={dialogTitle}
      onClose={onClose}
    >
      <div style={{ padding: '16px 0' }}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error updating the API settings:" />}

        {/* Max TPS Setting */}
        <TextInput
          disabled={isSubmitting}
          label="Max TPS (Transactions Per Second)"
          min="1"
          type="number"
          value={currentMaxTps?.toString() || 'Not configured'}
          onChange={handleMaxTpsChange}
        />
      </div>
    </Dialog>
  );
}
